import type { JSX, ReactElement, ComponentPropsWithRef } from 'react';
import { useContext, Children, cloneElement } from 'react';
import { EditableTextContext } from './EditableTextRoot';

export type EditableTextCancelButtonProps = {
  /**
   * A cancel button, typically a [Button]() that will revert the value when clicked.
   */
  children: ReactElement;
};

export const EditableTextCancelButton = ({
  children,
}: EditableTextCancelButtonProps): JSX.Element | null => {
  const context = useContext(EditableTextContext);
  if (!context) {
    throw new Error('EditableTextCancelButton must be used within an EditableTextRoot context');
  }
  const { setIsEditing, originalValue, setValue, onValueRevert, finalFocusEl, isEditing } = context;

  const handleClick = () => {
    setValue(originalValue);
    onValueRevert?.(originalValue);
    setIsEditing(false);
    setTimeout(() => {
      const element = finalFocusEl?.();
      if (element) {
        element.focus();
      }
    }, 0);
  };

  const onlyChild = Children.only(children);

  const triggerProps: Partial<ComponentPropsWithRef<'button'>> & {
    'data-wui-editable-text-cancel'?: boolean;
  } = {
    onClick: handleClick,
    'data-wui-editable-text-cancel': true,
  };

  if (!isEditing) {
    return null;
  }

  return cloneElement(onlyChild, triggerProps);
};
